import jsPDF from 'jspdf';
import 'jspdf-autotable';
import dayjs from 'dayjs';

/**
* Gerador de relatórios PDF para CSA
*/
class CSAPdfGenerator {
 constructor() {
   this.doc = null;
   this.currentY = 20;
   this.pageWidth = 210;
   this.pageHeight = 297;
   this.margins = {
     top: 20,
     right: 20,
     bottom: 20,
     left: 20
   };
   this.colors = {
     primary: '#1890ff',
     high: '#ff4d4f',
     medium: '#faad14',
     low: '#52c41a',
     info: '#1890ff',
     critical: '#ff4d4f',
     text: '#262626',
     lightText: '#8c8c8c',
     border: '#d9d9d9'
   };
 }

 /**
  * Inicializa um novo documento PDF
  */
 initDocument() {
   this.doc = new jsPDF('p', 'mm', 'a4');
   this.currentY = this.margins.top;
   
   this.doc.setFont('helvetica');
   
   return this.doc;
 }

 /**
  * Adiciona cabeçalho do relatório
  */
 addHeader(clienteNome, periodo, options) {
   const { doc } = this;
   
   doc.setFontSize(20);
   doc.setTextColor(this.colors.primary);
   doc.text('RELATÓRIO CSA', this.margins.left, this.currentY);
   
   this.currentY += 10;
   
   doc.setFontSize(14);
   doc.setTextColor(this.colors.text);
   doc.text('Cyber Security Assessment', this.margins.left, this.currentY);
   
   this.currentY += 15;
   
   doc.setFontSize(12);
   doc.setTextColor(this.colors.text);
   doc.text(`Cliente: ${clienteNome}`, this.margins.left, this.currentY);
   
   this.currentY += 7;
   doc.text(`Período: ${dayjs(periodo.inicio).format('DD/MM/YYYY')} a ${dayjs(periodo.fim).format('DD/MM/YYYY')}`, this.margins.left, this.currentY);
   
   this.currentY += 7;
   doc.text(`Gerado em: ${dayjs().format('DD/MM/YYYY HH:mm')}`, this.margins.left, this.currentY);
   
   if (options.severidades && options.severidades.length > 0 && options.severidades.length < 4) {
     this.currentY += 7;
     doc.setFontSize(10);
     doc.setTextColor(this.colors.lightText);
     const severidadesLabels = options.severidades.map(s => this.getSeverityLabel(s)).join(', ');
     doc.text(`Severidades incluídas: ${severidadesLabels}`, this.margins.left, this.currentY);
   }
   
   if (options.filtros && Object.keys(options.filtros).length > 0) {
     this.currentY += 7;
     doc.setFontSize(10);
     doc.setTextColor(this.colors.lightText);
     
     const filtrosTexto = [];
     if (options.filtros.host) filtrosTexto.push(`Host: ${options.filtros.host}`);
     if (options.filtros.cvssMinimo !== undefined && options.filtros.cvssMinimo !== null) {
       filtrosTexto.push(`CVSS Mín: ${options.filtros.cvssMinimo}`);
     }
     if (options.filtros.cveEspecifico) filtrosTexto.push(`CVE: ${options.filtros.cveEspecifico}`);
     if (options.filtros.portaEspecifica) filtrosTexto.push(`Porta: ${options.filtros.portaEspecifica}`);
     
     if (filtrosTexto.length > 0) {
       doc.text(`Filtros aplicados: ${filtrosTexto.join(' | ')}`, this.margins.left, this.currentY);
     }
   }
   
   this.currentY += 15;
   
   this.addHorizontalLine();
 }

 /**
  * Calcular estatísticas baseadas nos dados filtrados
  */
 calcularEstatisticasFiltradas(vulnerabilidades) {
   if (!vulnerabilidades || vulnerabilidades.length === 0) {
     return {
       estatisticasGerais: {
         totalVulnerabilidades: 0,
         hostsUnicos: 0,
         cvesUnicas: 0,
         scoreMediaCVSS: '0.0'
       },
       analisePorSeveridade: [
         { severity: 'high', count: 0, label: 'Alta', percentual: 0 },
         { severity: 'medium', count: 0, label: 'Média', percentual: 0 },
         { severity: 'low', count: 0, label: 'Baixa', percentual: 0 },
         { severity: 'info', count: 0, label: 'Informativa', percentual: 0 }
       ],
       scoreCVSS: {
         alto: 0,
         medio: 0,
         baixo: 0,
         informativo: 0
       },
       total: 0
     };
   }

   const distribuicaoSeveridade = {
     high: 0,
     medium: 0,
     low: 0,
     info: 0
   };
   
   const distribuicaoCVSS = {
     alto: 0,
     medio: 0,
     baixo: 0,
     informativo: 0
   };
   
   let somaScores = 0;
   let contadorScores = 0;
   const hostsUnicos = new Set();
   const cvesUnicas = new Set();
   
   vulnerabilidades.forEach(vuln => {
     const severity = vuln.severity?.toLowerCase() || 'info';
     if (distribuicaoSeveridade.hasOwnProperty(severity)) {
       distribuicaoSeveridade[severity]++;
     } else {
       distribuicaoSeveridade.info++;
     }
     
     const score = parseFloat(vuln.cvssScore);
     if (!isNaN(score)) {
       somaScores += score;
       contadorScores++;
       
       if (score >= 7.0) distribuicaoCVSS.alto++;
       else if (score >= 4.0) distribuicaoCVSS.medio++;
       else if (score >= 0.1) distribuicaoCVSS.baixo++;
       else distribuicaoCVSS.informativo++;
     }
     
     if (vuln.host) hostsUnicos.add(vuln.host);
     if (vuln.cve) cvesUnicas.add(vuln.cve);
   });
   
   const scoreMedia = contadorScores > 0 ? (somaScores / contadorScores).toFixed(1) : '0.0';
   
   return {
     estatisticasGerais: {
       totalVulnerabilidades: vulnerabilidades.length,
       hostsUnicos: hostsUnicos.size,
       cvesUnicas: cvesUnicas.size,
       scoreMediaCVSS: scoreMedia
     },
     analisePorSeveridade: Object.entries(distribuicaoSeveridade).map(([severity, count]) => ({
       severity,
       count,
       label: this.getSeverityLabel(severity),
       percentual: vulnerabilidades.length > 0 ? Math.round((count / vulnerabilidades.length) * 100) : 0
     })),
     scoreCVSS: distribuicaoCVSS,
     total: vulnerabilidades.length
   };
 }

 /**
  * Obter label da severidade
  */
 getSeverityLabel(severity) {
   const labels = {
     'high': 'Alta',
     'medium': 'Média',
     'low': 'Baixa',
     'info': 'Informativa'
   };
   return labels[severity] || severity;
 }

 /**
  * Adiciona resumo executivo
  */
 addResumoExecutivo(dashboardData, vulnerabilidadesFiltradas = null) {
   this.addSectionTitle('RESUMO EXECUTIVO');
   
   const usarDadosFiltrados = vulnerabilidadesFiltradas !== null && Array.isArray(vulnerabilidadesFiltradas);
   const dadosParaUsar = usarDadosFiltrados ? 
     this.calcularEstatisticasFiltradas(vulnerabilidadesFiltradas) : 
     dashboardData;
   
   const { estatisticasGerais, analisePorSeveridade } = dadosParaUsar;
   
   if (usarDadosFiltrados && vulnerabilidadesFiltradas.length !== dashboardData.estatisticasGerais.totalVulnerabilidades) {
     this.addText(
       `Este relatório foi gerado com filtros aplicados. Total de vulnerabilidades analisadas: ${vulnerabilidadesFiltradas.length} de ${dashboardData.estatisticasGerais.totalVulnerabilidades} vulnerabilidades totais do cliente.`, 
       false, 
       { color: this.colors.lightText, fontSize: 9 }
     );
     this.currentY += 5;
   }
   
   const stats = [
     ['Total de Vulnerabilidades:', estatisticasGerais.totalVulnerabilidades.toString()],
     ['Hosts Únicos Afetados:', estatisticasGerais.hostsUnicos.toString()],
     ['CVEs Únicas Identificadas:', estatisticasGerais.cvesUnicas.toString()],
     ['Score Médio CVSS:', estatisticasGerais.scoreMediaCVSS]
   ];
   
   this.addTable(stats, {
     headStyles: { fillColor: [24, 144, 255] },
     alternateRowStyles: { fillColor: [250, 250, 250] },
     showHead: false,
     columnStyles: {
       0: { fontStyle: 'bold', cellWidth: 80 },
       1: { cellWidth: 40 }
     }
   });
   
   this.currentY += 10;
   
   this.addSubsectionTitle('Distribuição por Severidade');
   
   const severityData = analisePorSeveridade
     .filter(item => item.count > 0)
     .map(item => [
       item.label,
       item.count.toString(),
       `${item.percentual}%`
     ]);
   
   if (severityData.length > 0) {
     this.addTable(severityData, {
       head: [['Severidade', 'Quantidade', 'Percentual']],
       headStyles: { fillColor: [24, 144, 255] },
       alternateRowStyles: { fillColor: [250, 250, 250] }
     });
   } else {
     this.addText('Nenhuma vulnerabilidade encontrada com os filtros aplicados.', false, { color: this.colors.lightText });
   }
 }

 /**
  * Adiciona análise de risco
  */
 addAnaliseRisco(dashboardData, vulnerabilidadesFiltradas = null, options = {}) {
   this.addSectionTitle('ANÁLISE DE RISCO');
   
   const usarDadosFiltrados = vulnerabilidadesFiltradas !== null && Array.isArray(vulnerabilidadesFiltradas);
   const dadosParaUsar = usarDadosFiltrados ? 
     this.calcularEstatisticasFiltradas(vulnerabilidadesFiltradas) : 
     dashboardData;
   
   const { scoreCVSS, analisePorSeveridade } = dadosParaUsar;
   
   const totalAltas = analisePorSeveridade.find(s => s.severity === 'high')?.count || 0;
   const totalMedias = analisePorSeveridade.find(s => s.severity === 'medium')?.count || 0;
   const totalBaixas = analisePorSeveridade.find(s => s.severity === 'low')?.count || 0;
   const totalInfo = analisePorSeveridade.find(s => s.severity === 'info')?.count || 0;
   
   let nivelRisco = 'INFORMATIVO';
   let corRisco = this.colors.info;
   
   if (totalAltas > 0) {
     nivelRisco = 'ALTO';
     corRisco = this.colors.high;
   } else if (totalMedias > 0) {
     nivelRisco = 'MÉDIO';
     corRisco = this.colors.medium;
   } else if (totalBaixas > 0) {
     nivelRisco = 'BAIXO';
     corRisco = this.colors.low;
   }
   
   this.doc.setFontSize(14);
   this.doc.setTextColor(corRisco);
   this.doc.text(`Nível de Risco Geral: ${nivelRisco}`, this.margins.left, this.currentY);
   this.currentY += 15;
   
   if (usarDadosFiltrados || (options.filtros && Object.keys(options.filtros).length > 0)) {
     this.doc.setFontSize(10);
     this.doc.setTextColor(this.colors.lightText);
     this.doc.text('* Análise baseada nos filtros aplicados', this.margins.left, this.currentY);
     this.currentY += 10;
   }
   
   const cvssData = [
     ['Alto (7.0-10.0)', scoreCVSS.alto.toString()],
     ['Médio (4.0-6.9)', scoreCVSS.medio.toString()],
     ['Baixo (0.1-3.9)', scoreCVSS.baixo.toString()],
     ['Informativo (0.0)', scoreCVSS.informativo.toString()]
   ];
   
   this.addTable(cvssData, {
     head: [['Score CVSS', 'Quantidade']],
     headStyles: { fillColor: [24, 144, 255] },
     alternateRowStyles: { fillColor: [250, 250, 250] }
   });
 }

  /**
   * Adiciona seção de vulnerabilidades detalhadas com limite configurável e paginação
   */
  addVulnerabilidadesDetalhadas(vulnerabilidades, options = {}) {
    this.addSectionTitle('VULNERABILIDADES DETALHADAS');
    
    if (!vulnerabilidades || vulnerabilidades.length === 0) {
      this.addText('Nenhuma vulnerabilidade encontrada com os filtros aplicados.', false, { color: this.colors.lightText });
      return;
    }
    
    const limite = options.filtros?.limiteDetalhes !== undefined ? 
      options.filtros.limiteDetalhes : 100;
    
    const vulnerabilidadesLimitadas = limite === -1 ? 
      vulnerabilidades : 
      vulnerabilidades.slice(0, limite);
    
    if (limite !== -1 && vulnerabilidades.length > limite) {
      this.addText(
        `Exibindo as ${limite} vulnerabilidades mais críticas de ${vulnerabilidades.length} total. ` +
        `As vulnerabilidades são ordenadas por score CVSS, severidade e data de detecção.`,
        false, 
        { color: this.colors.lightText, fontSize: 9 }
      );
      this.currentY += 10;
    } else if (limite === -1) {
      this.addText(
        `Exibindo todas as ${vulnerabilidades.length} vulnerabilidades encontradas com os filtros aplicados.`,
        false, 
        { color: this.colors.lightText, fontSize: 9 }
      );
      this.currentY += 10;
    }
    
    this.addText(`Total de ${vulnerabilidadesLimitadas.length} vulnerabilidades detalhadas:`, true);
    this.currentY += 5;
    
    const VULNERABILIDADES_POR_PAGINA = 5;
    let paginaAtual = 1;
    let vulnerabilidadesPorPagina = 0;
    
    vulnerabilidadesLimitadas.forEach((vuln, index) => {
      if (vulnerabilidadesPorPagina >= VULNERABILIDADES_POR_PAGINA) {
        this.doc.addPage();
        this.currentY = this.margins.top;
        paginaAtual++;
        vulnerabilidadesPorPagina = 0;
        
        this.doc.setFontSize(14);
        this.doc.setTextColor(this.colors.primary);
        this.doc.text(`VULNERABILIDADES DETALHADAS - Página ${paginaAtual}`, this.margins.left, this.currentY);
        this.currentY += 15;
        
        this.addHorizontalLine();
      }
      
      this.checkPageBreak(70);
      
      this.doc.setFontSize(12);
      this.doc.setTextColor(this.colors.text);
      this.doc.text(`${index + 1}. ${vuln.cve || 'CVE-PENDENTE'}`, this.margins.left, this.currentY);
      this.currentY += 8;
      
      const vulnInfo = [
        ['IP:', vuln.host || 'IP-Desconhecido'],
        ['IP:', vuln.ip || 'N/A'],
        ['Porta:', vuln.port || 'N/A'],
        ['Severidade:', this.getSeverityLabel(vuln.severity) || vuln.severity || 'N/A'],
        ['Score CVSS:', vuln.cvssScore || 'N/A'],
        ['CWE:', vuln.cwe || 'N/A']
      ];
      
      this.addTable(vulnInfo, {
        showHead: false,
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 30 },
          1: { cellWidth: 80 }
        },
        margin: { left: this.margins.left + 5 },
        styles: { fontSize: 8 }
      });
      
      this.currentY += 5;
      
      if (vuln.resume && vuln.resume.trim() !== '') {
        this.addText('Resumo:', true, { fontSize: 10 });
        const resumoLimitado = this.limitarTexto(vuln.resume, 200);
        this.addText(resumoLimitado, false, { maxWidth: 170, fontSize: 9 });
        this.currentY += 3;
      }
      
      if (vuln.suggestionsForCorrection && vuln.suggestionsForCorrection.trim() !== '') {
        this.addText('Sugestões para Correção:', true, { fontSize: 10 });
        const sugestoesLimitadas = this.limitarTexto(vuln.suggestionsForCorrection, 300);
        this.addText(sugestoesLimitadas, false, { maxWidth: 170, fontSize: 9 });
        this.currentY += 3;
      }
      
      if (index < vulnerabilidadesLimitadas.length - 1) {
        this.currentY += 3;
        this.doc.setDrawColor(this.colors.border);
        this.doc.line(
          this.margins.left + 10, 
          this.currentY, 
          this.pageWidth - this.margins.right - 10, 
          this.currentY
        );
        this.currentY += 5;
      } else {
        this.currentY += 5;
      }
      
      vulnerabilidadesPorPagina++;
    });
  }

 /**
  * Função auxiliar para limitar texto
  */
 limitarTexto(texto, maxChars) {
   if (!texto || texto.length <= maxChars) {
     return texto;
   }
   return texto.substring(0, maxChars) + '...';
 }

 /**
  * Calcular IPs mais vulneráveis dos dados filtrados
  */
 calcularHostsVulneraveisFromData(vulnerabilidades) {
   if (!vulnerabilidades || vulnerabilidades.length === 0) {
     return [];
   }

   const hostCount = {};
   vulnerabilidades.forEach(vuln => {
     const host = vuln.host || 'IP-Desconhecido';
     hostCount[host] = (hostCount[host] || 0) + 1;
   });

   return Object.entries(hostCount)
     .map(([host, count]) => ({ host, vulnerabilidades: count }))
     .sort((a, b) => b.vulnerabilidades - a.vulnerabilidades)
     .slice(0, 5);
 }

 /**
  * Adiciona seção de recomendações
  */
 addRecomendacoes(dashboardData, vulnerabilidadesFiltradas = null) {
   this.addSectionTitle('RECOMENDAÇÕES');
   
   const usarDadosFiltrados = vulnerabilidadesFiltradas !== null && Array.isArray(vulnerabilidadesFiltradas);
   const dadosParaUsar = usarDadosFiltrados ? 
     this.calcularEstatisticasFiltradas(vulnerabilidadesFiltradas) : 
     dashboardData;
   
   const { analisePorSeveridade } = dadosParaUsar;
   
   const recomendacoes = [];
   
   const altas = analisePorSeveridade.find(s => s.severity === 'high')?.count || 0;
   const medias = analisePorSeveridade.find(s => s.severity === 'medium')?.count || 0;
   const baixas = analisePorSeveridade.find(s => s.severity === 'low')?.count || 0;
   
   if (altas > 0) {
     recomendacoes.push({
       prioridade: 'CRÍTICA',
       texto: `Corrigir IMEDIATAMENTE as ${altas} vulnerabilidades de alta severidade identificadas. Estas representam riscos significativos à segurança e devem ter prioridade máxima.`
     });
   }
   
   if (medias > 0) {
     recomendacoes.push({
       prioridade: 'ALTA',
       texto: `Planejar correção das ${medias} vulnerabilidades de média severidade em até 30 dias. Estas vulnerabilidades podem ser exploradas e devem ser tratadas com urgência.`
     });
   }
   
   if (baixas > 0) {
     recomendacoes.push({
       prioridade: 'MÉDIA',
       texto: `Incluir as ${baixas} vulnerabilidades de baixa severidade no próximo ciclo de manutenção programada (até 90 dias).`
     });
   }
   
   if (usarDadosFiltrados && vulnerabilidadesFiltradas.length > 0) {
     const hostsMaisVulneraveis = this.calcularHostsVulneraveisFromData(vulnerabilidadesFiltradas);
     if (hostsMaisVulneraveis.length > 0) {
       const hostMaisVulneravel = hostsMaisVulneraveis[0];
       recomendacoes.push({
         prioridade: 'ALTA',
         texto: `Priorizar correções no host "${hostMaisVulneravel.host}" que concentra ${hostMaisVulneravel.vulnerabilidades} vulnerabilidades do escopo analisado.`
       });

       if (hostsMaisVulneraveis.length > 1) {
         const outrosHosts = hostsMaisVulneraveis.slice(1, 3).map(h => `${h.host} (${h.vulnerabilidades})`).join(', ');
         recomendacoes.push({
           prioridade: 'MÉDIA',
           texto: `Atenção especial também para: ${outrosHosts}.`
         });
       }
     }
   } else if (dashboardData.hostsVulneraveis && dashboardData.hostsVulneraveis.length > 0) {
     const hostMaisVulneravel = dashboardData.hostsVulneraveis[0];
     recomendacoes.push({
       prioridade: 'ALTA',
       texto: `Priorizar correções no host "${hostMaisVulneravel.host}" que possui ${hostMaisVulneravel.vulnerabilidades} vulnerabilidades.`
     });
   }
   
   if (altas > 0 || medias > 0) {
     recomendacoes.push({
       prioridade: 'ALTA',
       texto: 'Implementar processo de monitoramento contínuo de vulnerabilidades para detectar novas ameaças rapidamente.'
     });
     
     recomendacoes.push({
       prioridade: 'MÉDIA',
       texto: 'Estabelecer cronograma regular de aplicação de patches de segurança, com SLA diferenciado por severidade.'
     });
   }
   
   recomendacoes.push({
     prioridade: 'MÉDIA',
     texto: 'Realizar avaliações de segurança periódicas para identificar novas vulnerabilidades e validar correções aplicadas.'
   });
   
   recomendacoes.push({
     prioridade: 'BAIXA',
     texto: 'Revisar e atualizar políticas de segurança da informação, incluindo procedimentos de resposta a incidentes.'
   });

   if (dadosParaUsar.total === 0) {
     recomendacoes.splice(0, 0, {
       prioridade: 'MÉDIA',
       texto: 'Manter o bom nível de segurança atual através de monitoramento contínuo e atualizações regulares.'
     });
   }
   
   recomendacoes.forEach((rec, index) => {
     this.checkPageBreak(20);
     
     this.doc.setFontSize(10);
     const cor = rec.prioridade === 'CRÍTICA' ? this.colors.critical :
                  rec.prioridade === 'ALTA' ? this.colors.high :
                  rec.prioridade === 'MÉDIA' ? this.colors.medium : this.colors.low;
     
     this.doc.setTextColor(cor);
     this.doc.text(`[${rec.prioridade}]`, this.margins.left, this.currentY);
     
     this.doc.setTextColor(this.colors.text);
     this.addText(`${index + 1}. ${rec.texto}`, false, { 
       maxWidth: 160, 
       x: this.margins.left + 25,
       fontSize: 10
     });
     
     this.currentY += 5;
   });
 }

 /**
  * Adiciona observações do usuário
  */
 addObservacoes(observacoes) {
   if (!observacoes || observacoes.trim() === '') return;
   
   this.addSectionTitle('OBSERVAÇÕES');
   this.addText(observacoes, false, { maxWidth: 170 });
   this.currentY += 10;
 }

 /**
  * Adiciona rodapé
  */
 addFooter(options = {}) {
   const pageCount = this.doc.getNumberOfPages();
   
   for (let i = 1; i <= pageCount; i++) {
     this.doc.setPage(i);
     
     this.doc.setDrawColor(this.colors.border);
     this.doc.line(
       this.margins.left, 
       this.pageHeight - this.margins.bottom + 5,
       this.pageWidth - this.margins.right, 
       this.pageHeight - this.margins.bottom + 5
     );
     
     this.doc.setFontSize(8);
     this.doc.setTextColor(this.colors.lightText);
     
     this.doc.text(
       `Gerado em ${dayjs().format('DD/MM/YYYY HH:mm')}`,
       this.margins.left,
       this.pageHeight - this.margins.bottom + 10
     );
     
     const pageText = `Página ${i} de ${pageCount}`;
     const pageTextWidth = this.doc.getTextWidth(pageText);
     this.doc.text(
       pageText,
       (this.pageWidth - pageTextWidth) / 2,
       this.pageHeight - this.margins.bottom + 10
     );
     
     const classificacao = options.configuracoes?.classificacao?.toUpperCase() || 'CONFIDENCIAL';
     this.doc.text(
       classificacao,
       this.pageWidth - this.margins.right - this.doc.getTextWidth(classificacao),
       this.pageHeight - this.margins.bottom + 10
     );
   }
 }

 /**
  * Adiciona título de seção
  */
 addSectionTitle(title) {
   this.checkPageBreak(20);
   
   this.doc.setFontSize(16);
   this.doc.setTextColor(this.colors.primary);
   this.doc.text(title, this.margins.left, this.currentY);
   this.currentY += 12;
   
   this.addHorizontalLine();
 }

 /**
  * Adiciona título de subseção
  */
 addSubsectionTitle(title) {
   this.checkPageBreak(15);
   
   this.doc.setFontSize(12);
   this.doc.setTextColor(this.colors.text);
   this.doc.text(title, this.margins.left, this.currentY);
   this.currentY += 10;
 }

 /**
  * Adiciona texto com quebra de linha automática
  */
 addText(text, bold = false, options = {}) {
   const {
    maxWidth = this.pageWidth - this.margins.left - this.margins.right,
    fontSize = 10,
    x = this.margins.left,
    color = this.colors.text
  } = options;
  
  this.doc.setFontSize(fontSize);
  this.doc.setTextColor(color);
  
  if (bold) {
    this.doc.setFont('helvetica', 'bold');
  } else {
    this.doc.setFont('helvetica', 'normal');
  }
  
  const lines = this.doc.splitTextToSize(text, maxWidth);
  
  lines.forEach(line => {
    this.checkPageBreak(8);
    this.doc.text(line, x, this.currentY);
    this.currentY += 6;
  });
  
  this.currentY += 2;
}

/**
 * Adiciona tabela
 */
addTable(data, options = {}) {
  const defaultOptions = {
    startY: this.currentY,
    margin: { left: this.margins.left },
    styles: {
      fontSize: 9,
      cellPadding: 3
    },
    headStyles: {
      fillColor: [24, 144, 255],
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245]
   }
 };
 
 const finalOptions = { ...defaultOptions, ...options };
 
 this.doc.autoTable({
   body: data,
   ...finalOptions
 });
 
 this.currentY = this.doc.lastAutoTable.finalY + 10;
}

/**
* Adiciona linha horizontal
*/
addHorizontalLine() {
 this.doc.setDrawColor(this.colors.border);
 this.doc.line(
   this.margins.left, 
   this.currentY,
   this.pageWidth - this.margins.right, 
   this.currentY
 );
 this.currentY += 8;
}

/**
* Verifica se é necessário quebra de página
*/
checkPageBreak(spaceNeeded) {
 if (this.currentY + spaceNeeded > this.pageHeight - this.margins.bottom - 10) {
   this.doc.addPage();
   this.currentY = this.margins.top;
 }
}

/**
* Adiciona sumário/índice
*/
addSumario(options) {
 this.addSectionTitle('SUMÁRIO');
 
 const sections = [];
 
 if (options.incluirResumoExecutivo) {
   sections.push('Resumo Executivo');
 }
 
 sections.push('Análise de Risco');
 
 if (options.incluirDetalhes) {
   sections.push('Vulnerabilidades Detalhadas');
 }
 
 if (options.incluirRecomendacoes) {
   sections.push('Recomendações');
 }
 
 if (options.configuracoes?.observacoes) {
   sections.push('Observações');
 }
 
 sections.forEach((section, index) => {
   this.doc.setFontSize(11);
   this.doc.setTextColor(this.colors.text);
   this.doc.text(`${index + 1}. ${section}`, this.margins.left + 5, this.currentY);
   this.currentY += 8;
 });
 
 this.currentY += 10;
}

/**
* Adiciona estatísticas em formato de cards
*/
addStatsCards(estatisticas) {
 const cardWidth = 40;
 const cardHeight = 25;
 const spacing = 5;
 
 const stats = [
   { label: 'Total\nVulnerabilidades', value: estatisticas.totalVulnerabilidades, color: this.colors.high },
   { label: 'Hosts\nÚnicos', value: estatisticas.hostsUnicos, color: this.colors.info },
   { label: 'CVEs\nÚnicas', value: estatisticas.cvesUnicas, color: this.colors.medium },
   { label: 'Score Médio\nCVSS', value: estatisticas.scoreMediaCVSS, color: this.colors.primary }
 ];
 
 this.checkPageBreak(cardHeight + 10);
 
 const startX = this.margins.left;
 const startY = this.currentY;
 
 stats.forEach((stat, index) => {
   const x = startX + (index * (cardWidth + spacing));
   
   this.doc.setDrawColor(stat.color);
   this.doc.setLineWidth(0.5);
   this.doc.rect(x, startY, cardWidth, cardHeight);
   
   this.doc.setFontSize(16);
   this.doc.setTextColor(stat.color);
   this.doc.setFont('helvetica', 'bold');
   const valueWidth = this.doc.getTextWidth(stat.value.toString());
   this.doc.text(stat.value.toString(), x + (cardWidth - valueWidth) / 2, startY + 12);
   
   this.doc.setFontSize(8);
   this.doc.setTextColor(this.colors.text);
   this.doc.setFont('helvetica', 'normal');
   const lines = stat.label.split('\n');
   lines.forEach((line, lineIndex) => {
     const lineWidth = this.doc.getTextWidth(line);
     this.doc.text(line, x + (cardWidth - lineWidth) / 2, startY + 16 + (lineIndex * 4));
   });
 });
 
 this.currentY += cardHeight + 15;
}

/**
* Adiciona gráficos como imagens (se disponível)
*/
addChart(chartElement, title, width = 160, height = 80) {
 try {
   this.checkPageBreak(height + 20);
   
   if (title) {
     this.addSubsectionTitle(title);
   }
   
   if (chartElement && chartElement.toDataURL) {
     const imgData = chartElement.toDataURL('image/png');
     this.doc.addImage(imgData, 'PNG', this.margins.left, this.currentY, width, height);
     this.currentY += height + 10;
   }
 } catch (error) {
   this.addText(`[Gráfico: ${title}]`, true);
 }
}

/**
* Gera o relatório completo
*/
async gerarRelatorio(clienteNome, dashboardData, vulnerabilidades, options) {
 try {
   this.initDocument();
   
   this.addHeader(clienteNome, options.periodo, options);
   
   if (options.incluirIndice) {
     this.addSumario(options);
     this.currentY += 10;
   }
   
   if (options.incluirResumoExecutivo) {
     this.addResumoExecutivo(dashboardData, vulnerabilidades);
     this.currentY += 10;
   }
   
   if (options.incluirEstatisticas && vulnerabilidades && vulnerabilidades.length > 0) {
     const statsParaCards = this.calcularEstatisticasFiltradas(vulnerabilidades);
     this.addSubsectionTitle('Estatísticas Resumidas');
     this.addStatsCards(statsParaCards.estatisticasGerais);
     this.currentY += 10;
   }
   
   this.addAnaliseRisco(dashboardData, vulnerabilidades, options);
   this.currentY += 10;
   
   if (options.incluirDetalhes) {
     this.addVulnerabilidadesDetalhadas(vulnerabilidades, options);
     this.currentY += 10;
   }
   
   if (options.incluirRecomendacoes) {
     this.addRecomendacoes(dashboardData, vulnerabilidades);
     this.currentY += 10;
   }
   
   if (options.configuracoes?.observacoes) {
     this.addObservacoes(options.configuracoes.observacoes);
   }
   
   this.addFooter(options);
   
   const fileName = `CSA_Report_${clienteNome.replace(/[^a-zA-Z0-9]/g, '_')}_${dayjs().format('YYYY-MM-DD_HH-mm')}.pdf`;
   const pdfBlob = this.doc.output('blob');
   const pdfUrl = URL.createObjectURL(pdfBlob);
   
   window.open(pdfUrl, '_blank');

   return { success: true, fileName, pdfUrl };
   
 } catch (error) {
   return { success: false, error: error.message };
 }
}
}

export default CSAPdfGenerator;